import { useState } from "react";
import { api, isExpenseLinked } from "../lib/api";
import { toast } from "sonner";

/**
 * Shared "Push to Expenses" workflow used by the Payments board and the
 * Document Detail page. Manages the embedded-iframe link/bill state, the
 * loading guard, and the duplicate-push confirmation flow.
 *
 * Behavior mirrors the previous inline implementations exactly:
 *  - `openExpense(bill)`: fetches the prefill link (503 => integration disabled).
 *  - `requestPush(bill)`: opens directly, or asks for confirmation if the bill
 *    was already logged (`expense_post.status === "logged"`).
 *  - `confirmRePush()`: proceeds with the deferred push after confirmation.
 *
 * @param {() => void} [onLogged] callback fired after a successful expense log.
 */
export function useExpensePush(onLogged) {
  const [expenseLink, setExpenseLink] = useState(null);
  const [expenseBill, setExpenseBill] = useState(null);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [rePushTarget, setRePushTarget] = useState(null);

  const openExpense = async (bill) => {
    if (expenseLoading) return;
    setExpenseLoading(true);
    try {
      const res = await api.expenseLink(bill.id);
      setExpenseBill(bill);
      setExpenseLink(res.data);
    } catch (e) {
      if (e?.response?.status === 503) {
        toast.error("Expenses integration is not enabled.");
      } else {
        toast.error("Could not open the Expenses form.");
      }
    } finally {
      setExpenseLoading(false);
    }
  };

  // Guard against duplicate pushes: confirm before re-pushing an already-linked bill.
  const requestPush = (bill) => {
    if (isExpenseLinked(bill.expense_post)) {
      setRePushTarget(bill);
    } else {
      openExpense(bill);
    }
  };

  const confirmRePush = async () => {
    const bill = rePushTarget;
    setRePushTarget(null);
    if (bill) await openExpense(bill);
  };

  // Fallback confirmation: when the iframe postMessage can't reach us (e.g. a
  // non-production parent origin), ask our backend to verify with the Expenses
  // app whether the expense was actually saved, and link it if so. Retries a
  // couple of times to allow the Expenses save to commit. Only toasts when this
  // call actually flips the bill to "logged" (so it never duplicates the
  // postMessage success toast).
  const verifyLogged = async (bill) => {
    if (!bill || isExpenseLinked(bill.expense_post)) return;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await api.expenseVerify(bill.id);
        if (res.data?.verified) {
          if (res.data.changed) {
            const expenseId = res.data.bill?.expense_post?.expense_id;
            toast.success("Expense linked", {
              description: expenseId ? `Linked as ${expenseId}` : undefined,
            });
            onLogged && onLogged();
          }
          return;
        }
      } catch (e) {
        // Transient (network/timeout) — log at debug level and retry below.
        console.debug("expense-verify attempt failed; will retry", e);
      }
      if (attempt < 2) await new Promise((r) => setTimeout(r, 1500));
    }
  };

  const closeExpense = () => {
    const bill = expenseBill;
    setExpenseLink(null);
    setExpenseBill(null);
    verifyLogged(bill);
  };

  return {
    expenseLink,
    expenseBill,
    expenseLoading,
    rePushTarget,
    setRePushTarget,
    openExpense,
    requestPush,
    confirmRePush,
    closeExpense,
    onLogged,
  };
}
